import { FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';
import { OptionalLabel } from '../../../sharedComponents/OptionalLabel';
import { AccessoryType } from '../accessory.types';
import { ServiceOrder, ServiceOrderStatus } from '../serviceOrder.types';
import { HighlightedFormContainer } from '../../shared/sidebar/HighlightedFormContainer';
import { AccessoriesInput, InvalidAccessoriesAlert } from './AccessoriesInput';
import type {
    ServiceOrderExecutionDetailsFormData,
    ServiceOrderExecutionFormErrors,
} from './useServiceOrderExecutionForm';
import { ServiceDescription } from '../../shared/sidebar/ServiceDescription';
import { DayAndTimePicker } from '../../../sharedComponents/DayAndTimePicker';
import { getDateValidation, ServiceOrderDateField } from '../shared/ServiceOrderFormValidation';

const hasInvalidAccessories = (errors: ServiceOrderExecutionFormErrors) =>
    Object.keys(errors).some((key) => Object.values<string>(AccessoryType).includes(key));

type ServiceOrderSidebarBodyProps = {
    serviceOrder: ServiceOrder;
    setExecutionDetailsFormData: (formData: ServiceOrderExecutionDetailsFormData) => void;
    executionDetailsFormData: ServiceOrderExecutionDetailsFormData;
    formErrors: ServiceOrderExecutionFormErrors;
    setDateParseError: (field: ServiceOrderDateField, hasParseError: boolean) => void;
};

// eslint-disable-next-line max-lines-per-function
export const ServiceOrderSidebarBody: FunctionComponent<ServiceOrderSidebarBodyProps> = ({
    serviceOrder,
    executionDetailsFormData,
    setExecutionDetailsFormData,
    formErrors,
    setDateParseError,
}) => {
    const isDisabled =
        serviceOrder.status === ServiceOrderStatus.CANCELLED || serviceOrder.isFinalized || serviceOrder.isArchived;

    return (
        <HighlightedFormContainer instructionMessageId="outboundPortal.serviceOrder.sidebar.pleaseProvide">
            <div className="margin-top-20 margin-bottom-20">
                <ServiceDescription service={serviceOrder} />
            </div>
            {hasInvalidAccessories(formErrors) && (
                <div>
                    <InvalidAccessoriesAlert messageId="outboundPortal.common.invalidAccessory" />
                </div>
            )}
            {serviceOrder.vehicle.requestedAccessories.length > 0 && (
                <div className="margin-bottom-20">
                    <AccessoriesInput
                        requestedAccessories={serviceOrder.vehicle.requestedAccessories}
                        onChange={(accessories) =>
                            setExecutionDetailsFormData({ ...executionDetailsFormData, accessories })
                        }
                        collectedAccessoriesFormData={executionDetailsFormData.accessories}
                        accessoryErrors={formErrors}
                        isDisabled={isDisabled}
                    />
                </div>
            )}
            <div className="display-flex align-items-start flex-row flex-wrap gap-10 margin-bottom-20">
                <div className="flex-1-1 max-width-50pct">
                    <DayAndTimePicker
                        labelId="outboundPortal.serviceOrder.sidebar.executionStartDate"
                        timeZone={serviceOrder.serviceLocation.timeZone}
                        date={executionDetailsFormData.startedAt}
                        validation={getDateValidation(formErrors.startedAt)}
                        onDateChange={(startedAt) =>
                            setExecutionDetailsFormData({ ...executionDetailsFormData, startedAt })
                        }
                        isDisabled={isDisabled}
                        onParseError={(hasParseError) => setDateParseError('startedAt', hasParseError)}
                    />
                </div>

                <div className="flex-1-1 max-width-50pct">
                    <DayAndTimePicker
                        labelId="outboundPortal.serviceOrder.sidebar.executionEndDate"
                        timeZone={serviceOrder.serviceLocation.timeZone}
                        date={executionDetailsFormData.completedAt}
                        validation={getDateValidation(formErrors.completedAt)}
                        onDateChange={(date) =>
                            setExecutionDetailsFormData({ ...executionDetailsFormData, completedAt: date })
                        }
                        isDisabled={isDisabled}
                        onParseError={(hasParseError) => setDateParseError('completedAt', hasParseError)}
                    />
                </div>
            </div>
            <div>
                <OptionalLabel isOptional htmlFor="generalInformation">
                    <FormattedMessage id="outboundPortal.serviceOrder.sidebar.generalInformation" />
                </OptionalLabel>
                <textarea
                    id="generalInformation"
                    disabled={isDisabled}
                    className="width-100pct form-control"
                    style={{ resize: 'none' }}
                    rows={5}
                    maxLength={255}
                    value={executionDetailsFormData.generalInformation ?? ''}
                    onChange={(text) => {
                        setExecutionDetailsFormData({
                            ...executionDetailsFormData,
                            generalInformation: text.target.value,
                        });
                    }}
                />
            </div>
        </HighlightedFormContainer>
    );
};
